import { AfterViewInit, ChangeDetectorRef, Directive, ElementRef, HostListener, Renderer2 } from '@angular/core';
import { Subject } from 'rxjs';
import {
  NUMBER_0,
  NUMBER_16,
  NUMBER_2,
  NUMBER_200,
  NUMBER_230,
  NUMBER_3,
  NUMBER_4,
  NUMBER_500,
  NUMBER_600,
  NUMBER_768,
} from '~shared/constants';

@Directive({
  selector: '[appDesktopSwipe]',
})
export class DesktopSwipeDirective implements AfterViewInit {
  private carousel: HTMLElement;
  private scrollLeft = NUMBER_0;
  private arrowEnabled = true;
  private isDragging = false;
  private startX = 0;
  private scrollLeftStart = 0;
  private elementDraggable: HTMLElement;
  private leftArrow: HTMLElement;
  private rightArrow: HTMLElement;

  private destroySubject$ = new Subject<boolean>();
  private observer: MutationObserver;

  constructor(private el: ElementRef, private cd: ChangeDetectorRef, private renderer: Renderer2) {
    this.carousel = el.nativeElement;

    this.observer = new MutationObserver(() => {
      this.updateArrowVisibility();
    });
  }

  ngAfterViewInit() {
    this.leftArrow = this.carousel.querySelector('[data-name="left-arrow"]');
    this.rightArrow = this.carousel.querySelector('[data-name="right-arrow"]');

    this.setupDraggingEvents();
    this.updateArrowVisibility();

    if (this.leftArrow) {
      this.leftArrow.addEventListener('click', this.clickLeftArrow.bind(this));
    }

    if (this.rightArrow) {
      this.rightArrow.addEventListener('click', this.clickRightArrow.bind(this));
    }

    this.observer.observe(this.carousel, { childList: true, subtree: true });
  }

  ngOnDestroy() {
    this.observer.disconnect();
    this.destroySubject$.next();
    this.destroySubject$.complete();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: Event): void {
    const currentScrollLeft = this.carousel.scrollLeft;
    this.scrollLeft = currentScrollLeft;
    this.carousel.scrollLeft = currentScrollLeft;
    this.updateArrowVisibility();
  }

  // Dragging event handlers
  private setupDraggingEvents(): void {
    this.carousel.addEventListener('mousedown', this.startDragging.bind(this));
    this.carousel.addEventListener('mousemove', this.onDragging.bind(this));
    this.carousel.addEventListener('mouseup', this.stopDragging.bind(this));
    this.carousel.addEventListener('mouseleave', this.stopDragging.bind(this));
    this.carousel.addEventListener('scroll', this.updateArrowVisibility.bind(this));
  }

  private startDragging(event: MouseEvent): void {
    this.isDragging = true;
    this.elementDraggable = (event.target as HTMLElement).closest('.product-advanced-carousel__desktop__card');
    this.startX = event.pageX - this.carousel.offsetLeft;
    this.scrollLeftStart = this.carousel.scrollLeft;

    event.preventDefault();
  }

  private onDragging(event: MouseEvent): void {
    if (!this.isDragging) return;
    if (this.elementDraggable) {
      this.elementDraggable.style.pointerEvents = 'none';
    }
    const x = event.pageX - this.carousel.offsetLeft;
    const walk = (x - this.startX) * 1;
    this.scrollLeft = this.scrollLeftStart - walk;
    this.carousel.scrollLeft = this.scrollLeft;
  }

  private stopDragging(): void {
    this.isDragging = false;
    if (this.elementDraggable) {
      this.elementDraggable.style.pointerEvents = 'inherit';
    }
    this.elementDraggable = null;
  }

  // Visibility control methods
  private updateArrowVisibility(): void {
    this.scrollLeft = this.carousel.scrollLeft;
    const canScrollLeft = this.scrollLeft > NUMBER_0;
    const canScrollRight = this.carousel?.scrollWidth > this.carousel?.offsetWidth + this.scrollLeft + NUMBER_2;

    if (this.leftArrow) {
      this.renderer.setStyle(this.leftArrow, 'display', canScrollLeft ? '' : 'none');
    }
    if (this.rightArrow) {
      this.renderer.setStyle(this.rightArrow, 'display', canScrollRight ? '' : 'none');
    }
    this.cd.detectChanges();
  }

  // Helper methods
  private getScrollLeft(): number {
    const carouselWidth = this.carousel?.offsetWidth;
    const padding = NUMBER_16;
    const productsBySwipe =
      carouselWidth <= NUMBER_600
        ? NUMBER_2
        : carouselWidth > NUMBER_600 && carouselWidth <= NUMBER_768
        ? NUMBER_3
        : NUMBER_4;
    const cardWidth = carouselWidth <= NUMBER_768 ? NUMBER_200 : NUMBER_230;

    return (cardWidth + padding) * productsBySwipe;
  }

  // Arrow control methods
  private clickRightArrow(): void {
    if (this.arrowEnabled) {
      this.scrollCarousel(this.getScrollLeft(), 'right');
    }
  }

  private clickLeftArrow(): void {
    if (this.arrowEnabled) {
      this.scrollCarousel(this.getScrollLeft(), 'left');
    }
  }

  private scrollCarousel(scrollAmount: number, direction: 'left' | 'right'): void {
    this.arrowEnabled = false;
    this.carousel.style.scrollBehavior = 'smooth';

    this.scrollLeft =
      direction === 'right'
        ? Math.min(this.scrollLeft + scrollAmount, this.carousel.scrollWidth - this.carousel.clientWidth)
        : Math.max(this.scrollLeft - scrollAmount, NUMBER_0);

    this.carousel.scrollLeft = this.scrollLeft;

    setTimeout(() => {
      this.arrowEnabled = true;
      this.carousel.style.scrollBehavior = 'auto';
    }, NUMBER_500);
  }
}
